import { useEffect, useRef, useCallback, useState } from 'react';

export interface UseYouTubePlayerOptions {
  autoplay: boolean;
  loop: boolean;
  mute: boolean;
}

interface UseYouTubePlayerResult {
  isReady: boolean;
  isPlaying: boolean;
  error: string | null;
  restart: () => void;
}

/**
 * Detecta si el ID corresponde a una playlist.
 * Los playlist IDs de YouTube siempre tienen más de 11 caracteres Y empiezan
 * con un prefijo conocido (PL, UU, FL, RD, LL). Los video IDs son siempre
 * exactamente 11 caracteres, aunque por coincidencia empiecen con "PL".
 */
function isPlaylistId(videoId: string): boolean {
  return videoId.length > 11 && /^(PL|UU|FL|RD|LL)/.test(videoId);
}

/**
 * Agrega atributos `allow` al iframe generado por la YouTube IFrame API
 * para que el navegador permita autoplay, fullscreen, etc.
 */
function patchIframeAllow(elementId: string): void {
  const container = document.getElementById(elementId);
  const iframe = container?.tagName === 'IFRAME'
    ? container as HTMLIFrameElement
    : container?.querySelector('iframe');

  if (iframe) {
    iframe.setAttribute(
      'allow',
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
    );
    iframe.setAttribute('allowfullscreen', 'true');
    iframe.setAttribute('frameborder', '0');
  }
}

/**
 * Restaura el div contenedor si fue reemplazado por un iframe.
 */
function ensureContainerDiv(elementId: string): void {
  const el = document.getElementById(elementId);
  if (el && el.tagName === 'IFRAME') {
    const newDiv = document.createElement('div');
    newDiv.id = elementId;
    newDiv.className = 'full-screen-video';
    el.parentNode?.replaceChild(newDiv, el);
  }
}

export function useYouTubePlayer(
  elementId: string,
  videoId: string | null,
  options: UseYouTubePlayerOptions
): UseYouTubePlayerResult {
  const playerRef = useRef<YTPlayer | null>(null);
  const [isReady, setIsReady] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const videoIdRef = useRef(videoId);
  const optionsRef = useRef(options);
  const retryCountRef = useRef(0);
  const maxRetries = 3;

  // Mantener refs actualizadas
  useEffect(() => {
    videoIdRef.current = videoId;
    optionsRef.current = options;
  }, [videoId, options]);

  const restart = useCallback(() => {
    try {
      retryCountRef.current = 0;
      if (playerRef.current && videoIdRef.current) {
        const vid = videoIdRef.current;
        if (isPlaylistId(vid)) {
          playerRef.current.loadPlaylist(vid);
        } else {
          playerRef.current.loadVideoById(vid);
        }
        setError(null);
      }
    } catch (e) {
      console.error('Error restarting:', e);
      window.location.reload();
    }
  }, []);

  // Bloquear manipulación del navegador
  useEffect(() => {
    const blockContextMenu = (e: Event) => e.preventDefault();
    document.addEventListener('contextmenu', blockContextMenu);

    const blockKeyboard = (e: KeyboardEvent) => {
      const blocked = [
        'Space', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        'F5', 'F11', 'F12', 'Escape',
      ];
      if (
        blocked.includes(e.code) ||
        (e.ctrlKey && ['u', 's', 'i', 'j', 'c'].includes(e.key.toLowerCase())) ||
        (e.ctrlKey && e.shiftKey && ['i', 'j', 'c'].includes(e.key.toLowerCase()))
      ) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    document.addEventListener('keydown', blockKeyboard, true);

    const blockDrag = (e: Event) => e.preventDefault();
    document.addEventListener('dragstart', blockDrag);

    const blockDblClick = (e: Event) => e.preventDefault();
    document.addEventListener('dblclick', blockDblClick);

    const requestFullscreen = () => {
      const el = document.documentElement;
      if (document.fullscreenElement) return;
      const request =
        el.requestFullscreen?.bind(el) ??
        (el as any).webkitRequestFullscreen?.bind(el) ??
        (el as any).msRequestFullscreen?.bind(el);
      if (request) {
        request().catch(() => {});
      }
      document.removeEventListener('click', requestFullscreen);
      document.removeEventListener('touchstart', requestFullscreen);
    };
    document.addEventListener('click', requestFullscreen);
    document.addEventListener('touchstart', requestFullscreen);

    const onFullscreenChange = () => {
      if (!document.fullscreenElement) {
        setTimeout(() => {
          document.documentElement.requestFullscreen?.().catch(() => {});
        }, 500);
      }
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);

    return () => {
      document.removeEventListener('contextmenu', blockContextMenu);
      document.removeEventListener('keydown', blockKeyboard, true);
      document.removeEventListener('dragstart', blockDrag);
      document.removeEventListener('dblclick', blockDblClick);
      document.removeEventListener('click', requestFullscreen);
      document.removeEventListener('touchstart', requestFullscreen);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, []);

  // Inicializar reproductor de YouTube
  useEffect(() => {
    if (!videoId) return;

    const isPlaylist = isPlaylistId(videoId);
    const { autoplay, loop, mute } = options;

    // Reset retry count al cambiar de video
    retryCountRef.current = 0;

    const loadYouTubeAPI = (): Promise<void> => {
      return new Promise((resolve) => {
        if (window.YT && window.YT.Player) {
          resolve();
          return;
        }

        const existingScript = document.querySelector(
          'script[src="https://www.youtube.com/iframe_api"]'
        );

        if (existingScript) {
          const checkReady = setInterval(() => {
            if (window.YT && window.YT.Player) {
              clearInterval(checkReady);
              resolve();
            }
          }, 100);
          return;
        }

        const tag = document.createElement('script');
        tag.src = 'https://www.youtube.com/iframe_api';
        const firstScriptTag = document.getElementsByTagName('script')[0];
        firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

        window.onYouTubeIframeAPIReady = () => {
          resolve();
        };
      });
    };

    const destroyPlayer = () => {
      if (playerRef.current) {
        try {
          playerRef.current.destroy();
        } catch (e) {}
        playerRef.current = null;
      }
    };

    /**
     * Crea el reproductor con el host especificado.
     * Para playlists, usar directamente youtube.com (nocookie no funciona bien).
     * Para videos normales, intentar primero nocookie.
     */
    const createPlayer = (host: string) => {
      ensureContainerDiv(elementId);

      const element = document.getElementById(elementId);
      if (!element) {
        console.error(`Element #${elementId} not found`);
        return;
      }

      const shouldForceMute = autoplay ? true : mute;

      const basePlayerVars: Record<string, string | number> = {
        autoplay: autoplay ? 1 : 0,
        mute: shouldForceMute ? 1 : 0,
        controls: 0,
        rel: 0,
        showinfo: 0,
        modestbranding: 1,
        iv_load_policy: 3,
        disablekb: 1,
        fs: 0,
        playsinline: 1,
        origin: window.location.origin,
        enablejsapi: 1,
      };

      let playerOptions: YTPlayerOptions;

      if (isPlaylist) {
        playerOptions = {
          host,
          playerVars: {
            ...basePlayerVars,
            listType: 'playlist',
            list: videoId,
            loop: loop ? 1 : 0,
          },
          events: {
            onReady: handleReady,
            onStateChange: handleStateChange,
            onError: handleError,
          },
        };
      } else {
        playerOptions = {
          videoId: videoId,
          host,
          playerVars: {
            ...basePlayerVars,
            ...(loop ? { loop: 1, playlist: videoId } : {}),
          },
          events: {
            onReady: handleReady,
            onStateChange: handleStateChange,
            onError: handleError,
          },
        };
      }

      playerRef.current = new window.YT.Player(elementId, playerOptions);
    };

    const initPlayer = async () => {
      await loadYouTubeAPI();
      destroyPlayer();

      // Para playlists, ir directamente con youtube.com
      // ya que youtube-nocookie.com falla frecuentemente con playlists
      const initialHost = isPlaylist
        ? 'https://www.youtube.com'
        : 'https://www.youtube-nocookie.com';

      createPlayer(initialHost);
    };

    const handleReady = (event: YTPlayerEvent) => {
      setIsReady(true);
      setError(null);
      retryCountRef.current = 0;

      patchIframeAllow(elementId);

      if (autoplay) {
        event.target.playVideo();
      }
    };

    const handleStateChange = (event: YTPlayerEvent) => {
      switch (event.data) {
        case window.YT.PlayerState.PLAYING:
          setIsPlaying(true);
          setError(null);
          if (!mute && event.target.isMuted()) {
            event.target.unMute();
            event.target.setVolume(100);
          }
          break;
        case window.YT.PlayerState.PAUSED:
          setIsPlaying(false);
          if (autoplay) {
            setTimeout(() => {
              event.target.playVideo();
            }, 300);
          }
          break;
        case window.YT.PlayerState.ENDED:
          setIsPlaying(false);
          if (loop || autoplay) {
            event.target.playVideo();
          }
          break;
        case window.YT.PlayerState.BUFFERING:
          setIsPlaying(false);
          break;
        case window.YT.PlayerState.UNSTARTED:
          if (autoplay) {
            setTimeout(() => {
              event.target.playVideo();
            }, 500);
          }
          break;
      }
    };

    const handleError = (event: YTPlayerEvent) => {
      console.error('YouTube Player Error:', event.data, `(retry ${retryCountRef.current}/${maxRetries})`);

      const contentType = isPlaylist ? 'Playlist' : 'Video';

      // Para errores 101/150 (embebido no permitido), reintentar con host alternativo
      if ((event.data === 101 || event.data === 150) && retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        const retryMsg = `${contentType} no disponible para embebido. Reintentando (${retryCountRef.current}/${maxRetries})...`;
        setError(retryMsg);

        setTimeout(() => {
          destroyPlayer();

          // Alternar entre hosts en cada reintento
          const retryHost = retryCountRef.current % 2 === 0
            ? 'https://www.youtube-nocookie.com'
            : 'https://www.youtube.com';

          createPlayer(retryHost);
        }, 1500 * retryCountRef.current);
        return;
      }

      // Para otros errores, reintentar con loadVideoById/loadPlaylist
      let errorMsg = '';
      switch (event.data) {
        case 2:
          errorMsg = `ID de ${contentType.toLowerCase()} inválido`;
          break;
        case 5:
          errorMsg = 'Error de reproducción HTML5';
          break;
        case 100:
          errorMsg = `${contentType} no encontrado o privado`;
          break;
        case 101:
        case 150:
          errorMsg = `${contentType} no disponible para embebido`;
          break;
        default:
          errorMsg = `Error: ${event.data}`;
      }

      setError(errorMsg);

      // Para errores recuperables (no 101/150 que ya se manejaron), reintentar después de 5s
      if (event.data !== 101 && event.data !== 150 && retryCountRef.current < maxRetries) {
        retryCountRef.current++;
        setTimeout(() => {
          if (playerRef.current) {
            const vid = videoIdRef.current;
            if (vid) {
              if (isPlaylistId(vid)) {
                playerRef.current.loadPlaylist(vid);
              } else {
                playerRef.current.loadVideoById(vid);
              }
            }
          }
        }, 5000);
      }
    };

    initPlayer();

    return () => {
      destroyPlayer();
      setIsReady(false);
      setIsPlaying(false);
    };
    // eslint-disable-next-line
  }, [videoId, elementId, options.autoplay, options.loop, options.mute]);

  return { isReady, isPlaying, error, restart };
}
